import { useCallback, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useChatValidationStore } from 'src/hooks/chat';
import { audioPlayer, getAccessToken, logger } from 'src/services';
import { isFromCareConnect } from 'src/utils/url';

type Config = {
  language?: string;
  enabled: boolean;
  onError: (error: any) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
};

const isMediaSourceSupported = () => {
  return 'MediaSource' in window && MediaSource.isTypeSupported('audio/mpeg');
};

const reconnectionAttempts = 5;
const reconnectionDelay = 1000;
const reconnectionDelayMax = 5000;

const useTextToSpeech = ({
  language,
  enabled,
  onError,
  onConnect,
  onDisconnect,
}: Config) => {
  const { token } = useChatValidationStore();
  const socketRef = useRef<Socket>(null as any);
  const reconnectionAttemptRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    socketRef.current = io(
      import.meta.env.VITE_API_BASE_URL + '/text_to_speech',
      {
        path: '/vca/socket.io/',
        withCredentials: true,
        query: {
          language,
        },
        extraHeaders: {
          Authorization: `Bearer ${getAccessToken()}`,
          'From-Care-Connect': isFromCareConnect() ? 'true' : 'false',
          'Chat-URL': token ? `/${token}` : '/',
        },
        closeOnBeforeunload: true,
        reconnection: true, // Enable automatic reconnection
        autoConnect: true,
        reconnectionAttempts, // Maximum number of reconnection attempts
        reconnectionDelay, // Initial delay for reconnection (ms)
        reconnectionDelayMax, // Maximum delay between reconnections (ms)
      }
    );

    socketRef.current.on('connect', () => {
      onConnect?.();
    });

    socketRef.current.on('connect_error', (error) => {
      reconnectionAttemptRef.current += 1;
      logger.error(
        'useTextToSpeech: Socket connection error',
        {
          eventName: 'connect_error',
        },
        error
      );
      if (reconnectionAttemptRef.current >= reconnectionAttempts) {
        onError(error);
      }
    });

    socketRef.current.on('disconnect', () => {
      onDisconnect?.();
    });

    socketRef.current.on('audio_chunk', (data) => {
      audioPlayer.play(data);
    });

    socketRef.current.on('audio', (data) => {
      audioPlayer.play(data);
    });

    socketRef.current.on('error', (error) => {
      logger.error(
        'useTextToSpeech: Socket connection error',
        {
          eventName: 'error',
          message: error?.message,
        },
        error
      );
      onError(error);
    });

    audioPlayer.init();

    return () => {
      socketRef.current?.disconnect();
      audioPlayer.close();
    };
  }, [enabled]);

  const read = useCallback(
    (text: string) => {
      if (!enabled) return;

      if (isMediaSourceSupported()) {
        socketRef.current.emit('text_stream', text);
      } else {
        socketRef.current.emit('text', text);
      }
    },
    [enabled]
  );

  const stop = useCallback(() => {
    if (!enabled) return;

    audioPlayer.reset();
  }, [enabled]);

  return {
    read,
    stop,
  };
};

export { useTextToSpeech };
